// src/components/Footer.jsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaInstagram, FaTiktok, FaMapMarkerAlt, FaEnvelope, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';

import footerBgSmMd from '../assets/Sonic/59.jpeg';
import footerBgLg from '../assets/Sonic/59.jpeg';

// Motion variants (same as ContactUs)
const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const Footer = () => {
    const { t } = useTranslation('footer');
    const [email, setEmail] = React.useState('');

    const handleNewsletterSubmit = (e) => {
        e.preventDefault();
        if (email) {
            const message = `New Newsletter Subscriber: ${email}`;
            const whatsappUrl = `https://wa.me/94767584594?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
            setEmail('');
        }
    };

    return (
        <footer className="relative bg-[#001F3F] text-white overflow-hidden">
            {/* Background with overlay */}
            <div className="absolute inset-0">
                <div
                    className="absolute inset-0 bg-cover bg-center lg:hidden"
                    style={{ backgroundImage: `url(${footerBgSmMd})` }}
                />
                <div
                    className="absolute inset-0 bg-cover bg-center hidden lg:block"
                    style={{ backgroundImage: `url(${footerBgLg})`, backgroundAttachment: 'fixed' }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
            </div>

            <div className="relative z-10 container mx-auto px-6 sm:px-8 lg:px-16 py-16 sm:py-20 lg:py-28">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={sectionVariants}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16"
                >
                    {/* Brand & Navigation */}
                    <div className="space-y-8 text-center lg:text-left">
                        <h5
                            className="text-4xl lg:text-5xl font-bold text-[#7FDBFF] font-konkhmer tracking-tight"
                            dangerouslySetInnerHTML={{ __html: t('brand') }}
                        />
                        <ul className="space-y-4 font-inknut">
                            <li>
                                <NavLink
                                    to="/"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.home')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/about-us"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.about')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/srilanka"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.explore')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/tour"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.tours')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/gallery"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.gallery')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/events"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.events')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/things"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.things')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to="/contact-us"
                                    className="text-lg text-white/80 hover:text-[#7FDBFF] transition-all duration-300 hover:translate-x-1"
                                >
                                    {t('nav.contact')}
                                </NavLink>
                            </li>
                        </ul>
                    </div>

                    {/* Get in Touch */}
                    <div className="space-y-8 text-center lg:text-left">
                        <h5 className="text-3xl lg:text-4xl font-bold text-[#7FDBFF] font-konkhmer">
                            {t('contact.title')}
                        </h5>
                        <ul className="space-y-6 font-inknut">
                            <li className="flex items-start justify-center lg:justify-start gap-4">
                                <FaMapMarkerAlt className="w-6 h-6 text-[#0074D9] mt-1 flex-shrink-0" />
                                <span
                                    className="text-white/90 text-lg leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: t('contact.address') }}
                                />
                            </li>
                            <li className="flex items-center justify-center lg:justify-start gap-4">
                                <FaEnvelope className="w-6 h-6 text-[#0074D9] flex-shrink-0" />
                                <a
                                    href="mailto:sonictourssl@gmail.com"
                                    className="text-lg text-white/90 hover:text-[#7FDBFF] transition-all duration-300"
                                >
                                    {t('contact.email')}
                                </a>
                            </li>
                            <li className="flex items-center justify-center lg:justify-start gap-4">
                                <FaPhoneAlt className="w-6 h-6 text-[#0074D9] flex-shrink-0" />
                                <a
                                    href="tel:+94767584594"
                                    className="text-lg text-white/90 hover:text-[#7FDBFF] transition-all duration-300"
                                >
                                    {t('contact.phone')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Stay Connected & Newsletter */}
                    <div className="space-y-8 text-center lg:text-left">
                        <h5 className="text-3xl lg:text-4xl font-bold text-[#7FDBFF] font-konkhmer">
                            {t('newsletter.title')}
                        </h5>
                        <p className="text-white/80 text-lg font-inknut max-w-sm mx-auto lg:mx-0">
                            {t('newsletter.desc')}
                        </p>

                        {/* Newsletter Form */}
                        <form className="mt-6 flex flex-col sm:flex-row gap-4 max-w-md mx-auto lg:mx-0" onSubmit={handleNewsletterSubmit}>
                            <input
                                type="email"
                                placeholder={t('newsletter.placeholder')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 px-6 py-4 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#7FDBFF] transition-all duration-300 text-base"
                                required
                            />
                            <button
                                type="submit"
                                className="px-8 py-4 bg-gradient-to-r from-[#0074D9] to-[#7FDBFF] rounded-full font-bold text-white uppercase tracking-wider hover:brightness-110 hover:scale-105 transition-all duration-300 shadow-lg shadow-[#0074D9]/30"
                            >
                                {t('newsletter.subscribe')}
                            </button>
                        </form>

                        {/* Social Icons */}
                        <div className="flex justify-center lg:justify-start gap-6 mt-10">
                            <a
                                href="https://wa.me/+94767584594"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="WhatsApp"
                                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#0074D9]/30 transition-all duration-300 hover:scale-110"
                            >
                                <FaWhatsapp className="h-6 w-6 text-[#7FDBFF]" />
                            </a>
                            <a
                                href="https://www.instagram.com/sonic_tours.srilanka?igsh=c213Mjdob2t6aWQ%3D"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#0074D9]/30 transition-all duration-300 hover:scale-110"
                            >
                                <FaInstagram className="h-6 w-6 text-[#7FDBFF]" />
                            </a>
                            <a
                                href="mailto:sonictourssl@gmail.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Email"
                                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#0074D9]/30 transition-all duration-300 hover:scale-110"
                            >
                                <FaEnvelope className="h-6 w-6 text-[#7FDBFF]" />
                            </a>
                            <a
                                href="https://www.tiktok.com/@sonic_tours?lang=en-GB"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="TikTok"
                                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#0074D9]/30 transition-all duration-300 hover:scale-110"
                            >
                                <FaTiktok className="h-6 w-6 text-[#7FDBFF]" />
                            </a>
                        </div>
                    </div>

                    {/* Tagline – Desktop Only */}
                    <div className="hidden lg:block space-y-6">
                        <p
                            className="text-2xl font-light italic text-white/80 font-inknut leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: t('tagline') }}
                        />
                    </div>
                </motion.div>

                {/* Bottom Bar */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                    className="mt-16 pt-10 border-t border-white/10 text-center"
                >
                    <p className="text-white/70 text-base">
                        © {new Date().getFullYear()} <span className="text-[#7FDBFF] font-semibold">Sonic Tours</span>. {t('copyright')}
                    </p>
                    <p className="text-white/50 text-sm mt-3">
                        {t('developedBy')} <strong className="text-[#7FDBFF]">Redev Solutions</strong> (+94 76 460 8225)
                    </p>
                </motion.div>
            </div>
        </footer>
    );
};

export default Footer;